/**
 * 📐 **책을 기울이면 그림도 같이 기운다** — 카메라 AR (docs/build/35, FR-AR-09).
 *
 * ## 지금까지 왜 안 기울었나
 * 층 그림·말풍선은 3D 무대 **밖**(DOM)에 그린다. 그렇게 정한 이유가 있다: 무대 안에 넣었더니
 * 카메라가 흔들릴 때 **글자가 같이 흔들려** 아이가 못 읽었고, 「보이는데 안 눌리는」 자리가
 * 생겼다(이 저장소가 이미 겪은 사고). 대신 책을 비스듬히 들면 **그림만 정면을 보고 붙어 있다.**
 *
 * ## 그래서 절충한다
 * 글자는 그대로 평면에 두고, **층 그림만** 책을 따라 기울인다. 카메라가 알아본 그림의 네 귀퉁이가
 * 화면 어디에 있는지는 3D 쪽이 알려 준다 — 그 넷에 맞춰 DOM 상자를 **비틀면**(CSS `matrix3d`)
 * 3D 판을 쓰지 않고도 원근이 붙는다.
 *
 * ## 🔴 여기는 계산만
 * 네 귀퉁이를 어떻게 얻는지(three.js·MindAR)는 화면이 안다. 여기는 **네 점 → 비틀기 값**만
 * 만든다 — 순수 계산이라 카메라 없이도 검사할 수 있다(그리고 실제로 그렇게 검사한다).
 */

/** 화면 위의 한 점(픽셀). 왼쪽 위가 0,0. */
export interface TiltPoint {
  x: number;
  y: number;
}

/** 카메라가 알아본 그림의 네 귀퉁이 — **왼위 → 오른위 → 오른아래 → 왼아래** 차례. */
export type TiltCorners = [TiltPoint, TiltPoint, TiltPoint, TiltPoint];

/**
 * 8×8 연립방정식을 푼다(가우스 소거). 크기가 늘 8이라 라이브러리를 안 들인다.
 * 못 풀면(귀퉁이가 한 줄로 겹침 등) `null` — 부르는 쪽이 **기울이지 않고** 넘어간다.
 */
function solve8(a: number[][], b: number[]): number[] | null {
  const n = 8;
  const m = a.map((row, i) => [...row, b[i]!]);
  for (let c = 0; c < n; c++) {
    // 가장 큰 값을 골라 위로(작은 값으로 나누면 오차가 커진다)
    let piv = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(m[r]![c]!) > Math.abs(m[piv]![c]!)) piv = r;
    if (Math.abs(m[piv]![c]!) < 1e-9) return null; // 못 푼다 — 기울이지 않는다
    [m[c], m[piv]] = [m[piv]!, m[c]!];
    const p = m[c]![c]!;
    for (let k = c; k <= n; k++) m[c]![k]! /= p;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = m[r]![c]!;
      if (f === 0) continue;
      for (let k = c; k <= n; k++) m[r]![k]! -= f * m[c]![k]!;
    }
  }
  return m.map((row) => row[n]!);
}

/**
 * 상자(0,0)-(w,h) 의 네 귀퉁이를 화면의 네 점으로 보내는 **비틀기**.
 *
 * 돌려주는 것은 CSS `matrix3d(...)` 에 그대로 넣는 16개 값(열 우선).
 * `transform-origin: 0 0` 과 함께 써야 한다 — 안 그러면 가운데를 축으로 한 번 더 돈다.
 * 못 만들면 `null`(그 판은 그냥 안 기울인다 — 화면이 멈추면 안 된다).
 */
export function tiltMatrix(w: number, h: number, c: TiltCorners): number[] | null {
  if (!(w > 0) || !(h > 0)) return null;
  const src: TiltPoint[] = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x: sx, y: sy } = src[i]!;
    const { x: dx, y: dy } = c[i]!;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
    A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]);
    B.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]);
    B.push(dy);
  }
  const s = solve8(A, B);
  if (!s) return null;
  const [a, b, c0, d, e, f, g, i] = s as [number, number, number, number, number, number, number, number];
  // 3×3 비틀기를 CSS 4×4(열 우선)로. z 는 건드리지 않는다.
  const m = [a, d, 0, g, b, e, 0, i, 0, 0, 1, 0, c0, f, 0, 1];
  return m.every((v) => Number.isFinite(v)) ? m : null;
}

/**
 * 🔴 **얼마나 따라갈까** — 1 이면 그대로, 0 이면 평면 그대로.
 *
 * 카메라 추정은 손이 흔들리면 같이 떨린다. 아이가 보는 화면에서 그림이 파르르 떨면
 * 원근이 붙은 것보다 나쁘다. 그래서 네 귀퉁이를 **정면 상자 쪽으로 당겨** 섞는다.
 */
export function easeCorners(w: number, h: number, c: TiltCorners, strength: number): TiltCorners {
  const t = Math.max(0, Math.min(1, strength));
  const flat: TiltPoint[] = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  return c.map((p, i) => ({
    x: flat[i]!.x + (p.x - flat[i]!.x) * t,
    y: flat[i]!.y + (p.y - flat[i]!.y) * t,
  })) as TiltCorners;
}

/** 기울기 설정이 사는 자리(매니페스트). 기본은 **꺼짐** — 진짜 폰에서 보고 켜는 기능이다. */
export const AR_TILT_KEY = "ar.tilt";
export interface ArTiltSetting {
  on: boolean;
  /** 0~1. 1 이면 책을 그대로 따라간다. */
  strength: number;
}
export const AR_TILT_DEFAULT: ArTiltSetting = { on: false, strength: 0.8 };

/** 외부 입력 → 믿을 수 있는 설정(불변식 7). 모르는 값은 기본값으로. */
export function normalizeArTilt(raw: unknown): ArTiltSetting {
  const o = (raw ?? {}) as Record<string, unknown>;
  const s = typeof o.strength === "number" && Number.isFinite(o.strength) ? Math.max(0, Math.min(1, o.strength)) : AR_TILT_DEFAULT.strength;
  return { on: o.on === true, strength: s };
}

/**
 * 🎯 **카메라가 알아본 그림이 화면 어디에 있나** — 무대를 거기 얹기 위한 상자.
 *
 * ## 🔴 왜 필요한가 (2026-09-10 · 운영자가 폰으로 잡았다)
 * 카메라 AR 의 무대는 **화면 한가운데 고정된 상자**였다. 카메라가 책이 어디 있는지 이미
 * 알고 있는데 **그 값을 안 쓰고** 있었다. 그래서 아이가 책을 화면 한가운데에 딱 맞춰 들지
 * 않으면 층 그림이 **엉뚱한 자리**에 뜬다 — 쪽에 그려진 황남빵과 얹은 황남빵이 **둘 다 보였다.**
 * 첨성대도 같았다. 「AR 인데 안 붙어 있다」는 이 기능의 존재 이유를 깨는 결함이다.
 *
 * ## 왜 네 점의 **바깥 상자**가 아닌가
 * 책이 조금 돌아가 있으면 바깥 상자(axis-aligned bbox)는 책보다 **커진다** — 층이 통째로
 * 부푼다. 대신 **가운데는 네 점의 무게중심**, **크기는 마주 보는 변의 평균 길이**로 잡는다.
 * 정면일 때는 정확하고, 비스듬할 때는 책이 화면에서 실제로 차지하는 만큼 줄어든다.
 * (기울기까지 켜면 그 위에 `tiltMatrix` 가 원근을 얹는다 — 상자는 그 비틀기의 **기준**이다.)
 *
 * 못 믿을 값(무한대·너무 작음)이면 `null` — 부르는 쪽이 **예전처럼 가운데 상자**로 그린다.
 */
export interface ArStageBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 이보다 작으면 안 믿는다 — 인식이 막 잡혔거나 튄 값이다(층이 점으로 찌그러진다). */
export const AR_STAGE_MIN_PX = 24;

export function stageBoxFromCorners(c: TiltCorners | null | undefined): ArStageBox | null {
  if (!c || c.length !== 4) return null;
  if (!c.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return null;
  const [tl, tr, br, bl] = c;
  const len = (a: TiltPoint, b: TiltPoint) => Math.hypot(b.x - a.x, b.y - a.y);
  // 마주 보는 변의 평균 — 비스듬하면 짧아진 쪽이 그만큼 끌어내린다
  const width = (len(tl, tr) + len(bl, br)) / 2;
  const height = (len(tl, bl) + len(tr, br)) / 2;
  if (!(width >= AR_STAGE_MIN_PX) || !(height >= AR_STAGE_MIN_PX)) return null;
  const cx = (tl.x + tr.x + br.x + bl.x) / 4;
  const cy = (tl.y + tr.y + br.y + bl.y) / 4;
  return { left: cx - width / 2, top: cy - height / 2, width, height };
}
